<script setup>
import { ref, reactive, computed } from "vue";
import { apiQuery } from "@/api/j24api";
import { fetchAuthSession } from "aws-amplify/auth";
import { permissionsStore } from "@/stores/permissions";
const permissions = permissionsStore();

const config = reactive({
  url: "",
  username: "",
  password: "",
});

const currentStep = ref(1);
const loadingButton = ref(false);
const loadingDeploy = ref(false);

const showPolicySubscriptionPrompt = ref(false);
const showFailedCredentialDialog = ref(false);
const showWarningDialog = ref(false);
const showDeployDialog = ref(false);
const showCompletionDialog = ref(false);
const showCompletionWarningDialog = ref(false);
const showErrorActionDialog = ref(false);
const errorActionItem = ref(null);
let errorActionResolve = null;

const showSubscriptionDialog = ref(false);
const loadingSubscriptions = ref(false);
const eligibleSubscriptions = ref([]);
const resolvedMdmServerId = ref(null);

const search = ref("");
const isAdmin = ref(false);
const includeStagedItems = ref(false);
const stagedItemsArray = ref([]);

const itemArray = ref([]);
const productsArray = ref([]);

const selected = ref([]);
const selectedProduct = ref([]);
const selectedArray = ref([]);

const selectedTypeFilter = ref(["All"]);

// Deploy options
const disablePolicies = ref(false);
const removeScope = ref(false);
const updateExisting = ref(false);

const packageResolutions = ref([]);
const loadingResolutions = ref(false);

let deployedItemReport = [];

const steps = [
  { title: "Jamf Credentials", value: 1, complete: false },
  { title: "Products", subtitle: "Optional", value: 2, complete: false },
  { title: "Select Templates", value: 3, complete: false },
  { title: "Customise", subtitle: "Optional", value: 4, complete: false },
  { title: "Review & Deploy", value: 5, complete: false },
];

const itemHeaders = [
  { title: "Name", value: "name", sortable: true, width: "50%" },
  { title: "Type", value: "type", sortable: true, width: "50%" },
];

const productHeaders = [
  { title: "Name", value: "name", sortable: true },
  { title: "Description", value: "description", sortable: false },
  {
    title: "Created/Last Edited By",
    value: "latestEditor",
    sortable: true,
    width: "30%",
  },
];

const objectTypeDisplayNames = {
  policies: "Policies",
  scripts: "Scripts",
  packages: "Packages",
  computergroups: "Computer Groups",
  mobiledevicegroups: "Mobile Groups",
  osxconfigurationprofiles: "macOS Configuration Profiles",
  mobiledeviceconfigurationprofiles: "Mobile Configuration Profiles",
  computerextensionattributes: "macOS Extension Attributes",
  mobiledeviceextensionattributes: "Mobile Extension Attributes",
  categories: "Categories",
  dockitems: "Dock Items",
  diskencryptionconfigurations: "Disk Encryption Configurations",
  restrictedsoftware: "Restricted Software",
  advancedcomputersearches: "Advanced Computer Searches",
  advancedmobiledevicesearches: "Advanced Mobile Searches",
  networksegments: "Network Segments",
  "/v3/computer-prestages": "Computer Prestages",
  "/v2/mobile-device-prestages": "Mobile Device Prestages",
};

const dependencyIds = ref(new Set());

// Order in which types should be deployed (dependencies first)
const importOrder = [
  "dockitems",
  "categories",
  "packages",
  "networksegments",
  "computerextensionattributes",
  "mobiledeviceextensionattributes",
  "computergroups",
  "mobiledevicegroups",
  "scripts",
  "diskencryptionconfigurations",
  "osxconfigurationprofiles",
  "mobiledeviceconfigurationprofiles",
  "restrictedsoftware",
  "advancedcomputersearches",
  "advancedmobiledevicesearches",
  "policies",
  "/v3/computer-prestages",
  "/v2/mobile-device-prestages",
];

const deployHeaders = [
  { title: "Name", value: "name", width: "35%" },
  { title: "Type", value: "type", width: "20%" },
  { title: "Source", value: "source", width: "15%" },
  { title: "Status", value: "status", width: "15%" },
  { title: "Remove", value: "actions", width: "15%" },
];

const itemsPerPage = [
  { value: 25, title: "25" },
  { value: 50, title: "50" },
  { value: 100, title: "100" },
  { value: -1, title: "All" },
];

const typeFilterOptions = computed(() => {
  const types = new Set(allDisplayItems.value.map((item) => item.type));
  return ["All", ...Array.from(types).sort()];
});

const allDisplayItems = computed(() => {
  if (includeStagedItems.value) {
    return [...itemArray.value, ...stagedItemsArray.value];
  }
  return itemArray.value;
});

const filteredItems = computed(() => {
  if (selectedTypeFilter.value[0] === "All") {
    return allDisplayItems.value;
  }
  return allDisplayItems.value.filter((item) =>
    selectedTypeFilter.value.includes(item.type)
  );
});

async function getPortalUser() {
  try {
    const session = await fetchAuthSession();
    return session.tokens.idToken.payload.email;
  } catch {
    return "Unknown";
  }
}

async function verifyCredentials() {
  loadingButton.value = true;

  const result = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "credentialsCheck",
    config: {
      url: config.url.replace(/\/$/, ""),
      username: config.username,
      password: config.password,
    },
  });

  if (result === "Success") {
    await getData();
    nextStep();
  } else {
    showFailedCredentialDialog.value = true;
  }

  loadingButton.value = false;
}

async function getData() {
  let items = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "getItems",
  });

  if (Array.isArray(items)) {
    items.forEach((item) => {
      item.id = Math.floor(Math.random() * 10000000000000000000);
      item.isStaged = false;
      itemArray.value.push(item);
    });
  }

  let products = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "getProducts",
  });

  if (Array.isArray(products)) {
    products.forEach((product) => {
      product.id = Math.floor(Math.random() * 10000000000000000000);
      productsArray.value.push(product);
    });
  }

  // Check if user is a template admin
  const adminResult = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "isTemplateAdmin",
  });
  isAdmin.value = adminResult === true;

  // If admin, fetch all staged items
  if (isAdmin.value) {
    let staged = await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "getAllStagedItems",
    });

    if (Array.isArray(staged)) {
      staged.forEach((item) => {
        item.id = Math.floor(Math.random() * 10000000000000000000);
        item.isStaged = true;
        stagedItemsArray.value.push(item);
      });
    }
  }
}

function nextStep() {
  if (currentStep.value < steps.length) {
    steps[currentStep.value - 1].complete = true;

    if (currentStep.value === 2) {
      // Process product selections
      matchProductItems();
    }

    if (currentStep.value === 3) {
      resolveDependencies();
      getSelectedItems();

      if (getSelectedItemsDetails.value.length === 0) {
        showWarningDialog.value = true;
        return;
      }
    }

    if (currentStep.value === 4) {
      fetchPackageResolutions();
    }

    currentStep.value++;
  }
}

function prevStep() {
  if (currentStep.value > 1) {
    currentStep.value--;
    steps[currentStep.value - 1].complete = false;
  }

  if (currentStep.value === 2) {
    selected.value = [];
    selectedProduct.value = [];
    dependencyIds.value = new Set();
  }

  if (currentStep.value === 3) {
    // Remove auto-added dependencies so they can be recalculated
    selected.value = selected.value.filter(
      (id) => !dependencyIds.value.has(id)
    );
    dependencyIds.value = new Set();
  }
}

const getSelectedItemsDetails = computed(() => {
  const uniqueItemIds = new Set();
  const allItems = [...itemArray.value, ...stagedItemsArray.value];
  return selected.value
    .map((id) => {
      const selectedItem = allItems.find((item) => item.id === id);
      if (selectedItem && !uniqueItemIds.has(id)) {
        uniqueItemIds.add(id);
        const { _id, name, type, template, isStaged, author } = selectedItem;
        return {
          _id,
          name,
          type,
          template,
          isStaged: isStaged || false,
          author: author || null,
          isDependency: dependencyIds.value.has(id),
          status: "Pending",
        };
      }
    })
    .filter(Boolean);
});

const hasDeployedPolicies = computed(() =>
  selectedArray.value.some(
    (item) => item.type === "policies" && item.status === "Complete"
  )
);

function getSelectedItems() {
  const items = [...getSelectedItemsDetails.value];
  items.sort((a, b) => {
    const orderA = importOrder.indexOf(a.type);
    const orderB = importOrder.indexOf(b.type);
    return (orderA === -1 ? 999 : orderA) - (orderB === -1 ? 999 : orderB);
  });
  selectedArray.value = items;
  return selectedArray.value;
}

function resolveDependencies() {
  const allItems = [...itemArray.value, ...stagedItemsArray.value];
  const visited = new Set();
  const toProcess = [...selected.value];

  while (toProcess.length > 0) {
    const id = toProcess.pop();
    if (visited.has(id)) continue;
    visited.add(id);

    const item = allItems.find((i) => i.id === id);
    if (!item || !Array.isArray(item.dependencies)) continue;

    for (const dep of item.dependencies) {
      const match = allItems.find(
        (i) => i.name === dep.name && i.type === dep.type
      );
      if (match && !selected.value.includes(match.id)) {
        selected.value.push(match.id);
        dependencyIds.value.add(match.id);
        toProcess.push(match.id);
      }
    }
  }
}

function matchProductItems() {
  selectedProduct.value.forEach((productId) => {
    const product = productsArray.value.find((p) => p.id === productId);
    if (product && product.items) {
      for (const type in product.items) {
        product.items[type].forEach((name) => {
          const matchingItem = itemArray.value.find(
            (item) => item.name === name && item.type === type
          );
          if (matchingItem && !selected.value.includes(matchingItem.id)) {
            selected.value.push(matchingItem.id);
          }
        });
      }
    }
  });
}

async function fetchPackageResolutions() {
  const items = selectedArray.value
    .filter((item) => item.type === "packages" || item.type === "policies")
    .map((item) => ({
      name: item.name,
      type: item.type,
      stagingAuthor: item.isStaged ? item.author : null,
    }));

  if (items.length === 0) {
    packageResolutions.value = [];
    return;
  }

  loadingResolutions.value = true;
  try {
    const result = await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "previewPackageResolutions",
      items: items,
    });

    if (Array.isArray(result)) {
      packageResolutions.value = result;
    } else {
      packageResolutions.value = [];
    }
  } catch {
    packageResolutions.value = [];
  }
  loadingResolutions.value = false;
}

function deleteItem(item) {
  const index = selectedArray.value.indexOf(item);
  if (index > -1) {
    selectedArray.value.splice(index, 1);
  }
}

function waitForErrorAction() {
  return new Promise((resolve) => {
    errorActionResolve = resolve;
  });
}

function handleErrorAction(action) {
  showErrorActionDialog.value = false;
  if (errorActionResolve) {
    errorActionResolve(action);
    errorActionResolve = null;
  }
}

async function deployItem(item) {
  item.status = "In Progress";

  const options = {
    disablePolicies: disablePolicies.value,
    removeScope: removeScope.value,
    updateExisting: updateExisting.value,
  };

  if (item.isStaged && item.author) {
    options.stagingAuthor = item.author;
  }

  const result = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "import",
    config: {
      url: config.url.replace(/\/$/, ""),
      username: config.username,
      password: config.password,
    },
    items: [{ name: item.name, type: item.type }],
    options: options,
  });

  if (result && Array.isArray(result)) {
    const itemResult = result[0];
    if (
      itemResult &&
      (itemResult.status === "Created" || itemResult.status === "Updated")
    ) {
      item.status = "Complete";
    } else if (itemResult && itemResult.status === "Exists") {
      item.status = "Exists";
    } else {
      item.status = "Error";
      item.errorDetail = itemResult?.detail || "Unknown error";
    }
  } else {
    item.status = "Error";
    item.errorDetail = "No response from server";
  }
}

async function deploySelectedItems() {
  showDeployDialog.value = false;
  loadingDeploy.value = true;

  for (let i = 0; i < selectedArray.value.length; i++) {
    const item = selectedArray.value[i];

    if (item.status === "Complete" || item.status === "Skipping") {
      item.status = "Skipping";
      continue;
    }

    await deployItem(item);

    if (item.status === "Error") {
      errorActionItem.value = item;
      showErrorActionDialog.value = true;
      const action = await waitForErrorAction();

      if (action === "retry") {
        item.status = "Pending";
        item.errorDetail = null;
        i--;
        continue;
      } else if (action === "stop") {
        break;
      }
      // "continue" falls through to next item
    }

    deployedItemReport.push({
      name: item.name,
      type: item.type,
      status: item.status,
      detail: item.errorDetail || null,
    });
  }

  await sendLog();

  loadingDeploy.value = false;

  if (selectedArray.value.some((item) => item.status === "Error")) {
    showCompletionWarningDialog.value = true;
  } else {
    showCompletionDialog.value = true;
  }

  const finalStepIndex = steps.findIndex((step) => step.value === 5);
  if (finalStepIndex !== -1) {
    steps[finalStepIndex].complete = true;
  }
}

async function sendLog() {
  const portalUser = await getPortalUser();

  const logObject = {
    mdm: "Jamf",
    logType: "Deploy",
    jamfInstance: config.url,
    jamfApiUser: config.username,
    portalUser: portalUser,
    timestamp: new Date().toISOString(),
    log: deployedItemReport,
  };

  await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "log",
    payload: logObject,
  });

  deployedItemReport = [];
}

async function checkForEligibleSubscriptions() {
  const deployedPolicies = selectedArray.value.filter(
    (item) => item.type === "policies" && item.status === "Complete"
  );
  if (deployedPolicies.length === 0) return;

  loadingSubscriptions.value = true;
  try {
    const normalizedUrl = config.url.replace(/\/+$/, "");
    const serverResult = await apiQuery("ELA", "dashboard/updatepolicies", {
      apiAction: "GetMdmServerByUrl",
      jamfURL: normalizedUrl,
    });

    if (!serverResult || !serverResult.mdmServerId) {
      loadingSubscriptions.value = false;
      return;
    }
    resolvedMdmServerId.value = serverResult.mdmServerId;

    const updatablePolicies = await apiQuery(
      "ELA",
      "dashboard/updatepolicies",
      {
        apiAction: "GetUpdatablePolicies",
        mdmServerId: serverResult.mdmServerId,
        jamfURL: normalizedUrl,
      }
    );

    if (!Array.isArray(updatablePolicies) || updatablePolicies.length === 0) {
      loadingSubscriptions.value = false;
      return;
    }

    const deployedNames = new Set(
      deployedPolicies.map((p) => p.name.toLowerCase())
    );
    const eligible = updatablePolicies
      .filter((p) => deployedNames.has(p.name.toLowerCase()))
      .filter((p) => !p.subExists)
      .map((p) => ({
        ...p,
        selected: true,
        selectedMatch: p.matches[0],
      }));

    if (eligible.length > 0) {
      eligibleSubscriptions.value = eligible;
    }
  } catch (error) {
    console.log("Subscription check failed:", error);
  }
  loadingSubscriptions.value = false;
}

async function addSelectedSubscriptions() {
  const subscriptionsToAdd = [];
  eligibleSubscriptions.value.forEach((policy) => {
    if (policy.selected && policy.selectedMatch) {
      subscriptionsToAdd.push({
        mdmServerId: resolvedMdmServerId.value,
        policyID: policy.id,
        policyName: policy.name,
        patchName: policy.selectedMatch.name,
        patchStream: policy.selectedMatch.stream,
      });
    }
  });

  if (subscriptionsToAdd.length === 0) {
    showSubscriptionDialog.value = false;
    return;
  }

  loadingSubscriptions.value = true;
  try {
    await apiQuery("ELA", "dashboard/updatepolicies", {
      apiAction: "AddPolicySubscription",
      policies: subscriptionsToAdd,
    });
    showSubscriptionDialog.value = false;
  } catch (error) {
    console.log("Failed to add subscriptions:", error);
  }
  loadingSubscriptions.value = false;
}

async function handleCheckSubscriptions() {
  showCompletionDialog.value = false;
  showCompletionWarningDialog.value = false;
  showSubscriptionDialog.value = true;
  await checkForEligibleSubscriptions();
}

function getRowProps({ item }) {
  if (item.isStaged) {
    return { class: "staged-row" };
  }
  return {};
}

function changeStatusColour(status) {
  switch (status) {
    case "Pending":
      return "grey";
    case "In Progress":
      return "orange";
    case "Complete":
      return "green";
    case "Exists":
      return "blue";
    case "Error":
      return "red";
    case "Skipping":
      return "grey";
    default:
      return "grey";
  }
}
</script>

<template>
  <v-stepper v-model="currentStep">
    <v-alert
      v-if="currentStep > 1"
      type="info"
      variant="tonal"
      density="compact"
      class="mx-4 mt-3"
    >
      <span class="font-weight-bold">Connected to:</span> {{ config.url }}
    </v-alert>
    <v-stepper-header>
      <v-stepper-item
        v-for="(step, index) in steps"
        :key="index"
        :title="step.title"
        :value="step.value"
        :subtitle="step.subtitle"
        :complete="step.complete"
      ></v-stepper-item>
    </v-stepper-header>
    <v-stepper-content
      v-for="(step, index) in steps"
      :key="index"
      :step="step.value"
    >
      <v-sheet v-if="step.value === currentStep">
        <v-card-text>
          <!-- Step 1: Jamf Credentials -->
          <template v-if="step.value === 1">
            <v-card max-width="500" variant="elevated" class="mx-auto">
              <v-card-text>
                <v-table class="text-left">
                  <v-responsive class="mx-auto py-2" max-width="500">
                    <v-text-field
                      clearable
                      v-model="config.url"
                      label="Target Jamf Pro URL"
                      type="input"
                      hint="e.g. https://yourinstance.jamfcloud.com"
                      variant="outlined"
                    ></v-text-field>
                  </v-responsive>
                  <v-responsive class="mx-auto py-2" max-width="500">
                    <v-text-field
                      clearable
                      v-model="config.username"
                      label="API Username"
                      type="input"
                      variant="outlined"
                    ></v-text-field>
                  </v-responsive>
                  <v-responsive class="mx-auto py-2" max-width="500">
                    <v-text-field
                      clearable
                      v-model="config.password"
                      label="API Password"
                      type="password"
                      variant="outlined"
                    ></v-text-field>
                  </v-responsive>
                  <v-btn
                    block
                    class="text-none mb-4"
                    size="x-large"
                    @click="verifyCredentials"
                    :disabled="
                      !config.url ||
                      !config.username ||
                      !config.password ||
                      loadingButton
                    "
                    :loading="loadingButton"
                    variant="tonal"
                    color="darkgrey"
                    text="Validate Credentials"
                  ></v-btn>
                </v-table>
              </v-card-text>
            </v-card>
          </template>

          <!-- Step 2: Products -->
          <template v-if="step.value === 2">
            <h2>Select any optional products</h2>
            <p class="text-body-2 mb-4">
              Selecting a product will pre-select all of its templates in the
              next step.
            </p>
            <v-data-table
              :headers="productHeaders"
              :items="productsArray"
              item-key="id"
              show-select
              v-model="selectedProduct"
              class="elevation-2"
              show-expand
              :items-per-page="25"
              :items-per-page-options="itemsPerPage"
              no-data-text="No Jamf products available"
            >
              <template v-slot:expanded-row="{ columns, item }">
                <tr>
                  <td :colspan="columns.length">
                    <table class="expanded-row">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template
                          v-for="(names, type) in item.items"
                          :key="type"
                        >
                          <tr v-for="name in names" :key="name">
                            <td>{{ name }}</td>
                            <td>
                              {{ objectTypeDisplayNames[type] || type }}
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </template>

          <!-- Step 3: Select Templates -->
          <template v-if="step.value === 3">
            <h2>Select templates to deploy</h2>
            <v-responsive>
              <div class="d-flex align-center">
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search"
                  variant="underlined"
                  class="input-field"
                  style="margin-right: 20px"
                ></v-text-field>
                <v-select
                  v-model="selectedTypeFilter"
                  :items="typeFilterOptions"
                  chips
                  multiple
                  label="Type"
                  variant="underlined"
                  class="input-field"
                  style="max-width: 300px"
                ></v-select>
                <v-switch
                  v-if="isAdmin"
                  v-model="includeStagedItems"
                  label="Include Staged Items"
                  color="orange"
                  hide-details
                  class="ml-4 flex-shrink-0"
                ></v-switch>
              </div>
            </v-responsive>
            <v-data-table
              :headers="itemHeaders"
              :items="filteredItems"
              :search="search"
              item-key="id"
              show-select
              v-model="selected"
              class="elevation-2"
              :items-per-page="25"
              :items-per-page-options="itemsPerPage"
              :row-props="getRowProps"
            >
              <template #[`item.name`]="{ item }">
                {{ item.name }}
                <v-chip
                  v-if="item.isStaged"
                  size="x-small"
                  color="orange"
                  class="ml-2"
                >
                  Staged
                </v-chip>
              </template>
            </v-data-table>
          </template>

          <!-- Step 4: Customise -->
          <template v-if="step.value === 4">
            <h2>Deployment Options</h2>
            <v-row class="mt-2">
              <v-col cols="12" md="6">
                <v-card variant="elevated">
                  <v-card-title>Policy Settings</v-card-title>
                  <v-card-text>
                    <v-switch
                      label="Disable all policies on import"
                      inset
                      color="primary"
                      v-model="disablePolicies"
                      hint="Policies will be created in a disabled state"
                      persistent-hint
                    ></v-switch>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="6">
                <v-card variant="elevated">
                  <v-card-title>Profile Settings</v-card-title>
                  <v-card-text>
                    <v-switch
                      label="Remove scope from configuration profiles"
                      inset
                      color="primary"
                      v-model="removeScope"
                      hint="Profiles will be created without any scope assignments"
                      persistent-hint
                    ></v-switch>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="6">
                <v-card variant="elevated">
                  <v-card-title>Existing Objects</v-card-title>
                  <v-card-text>
                    <v-switch
                      label="Update existing objects if they already exist"
                      inset
                      color="primary"
                      v-model="updateExisting"
                      hint="If an object with the same name exists, it will be updated"
                      persistent-hint
                    ></v-switch>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <!-- Step 5: Review & Deploy -->
          <template v-if="step.value === 5">
            <h2>Review your selected items before deploying</h2>
            <v-alert
              v-if="loadingResolutions"
              type="info"
              variant="tonal"
              class="my-3"
            >
              <v-progress-circular
                indeterminate
                size="16"
                width="2"
                class="mr-2"
              ></v-progress-circular>
              Checking for package version updates...
            </v-alert>
            <v-alert
              v-else-if="packageResolutions.length > 0"
              type="info"
              variant="tonal"
              class="my-3"
            >
              <div class="font-weight-bold mb-2">Package Version Updates</div>
              <div class="text-body-2">
                The following packages will be updated to the latest approved
                version during deployment:
              </div>
              <div
                v-for="(res, index) in packageResolutions"
                :key="index"
                class="text-body-2 mt-1"
              >
                <v-icon size="small" class="mr-1">mdi-package-variant</v-icon>
                <strong>{{ res.original }}</strong>
                <v-icon size="small" class="mx-1">mdi-arrow-right</v-icon>
                <strong>{{ res.resolved }}</strong>
                <span class="text-medium-emphasis ml-1">
                  (in {{ res.templateName }})
                </span>
              </div>
            </v-alert>
            <div style="margin-bottom: 10px"></div>
            <v-data-table
              :headers="deployHeaders"
              :items="selectedArray"
              item-key="name"
              :items-per-page="-1"
              :items-per-page-options="[{ value: -1, title: 'All' }]"
              class="elevation-2"
              :row-props="getRowProps"
            >
              <template #[`item.name`]="{ item }">
                {{ item.name }}
                <v-chip
                  v-if="item.isStaged"
                  size="x-small"
                  color="orange"
                  class="ml-2"
                >
                  Staged
                </v-chip>
              </template>
              <template #[`item.source`]="{ item }">
                <v-chip
                  v-if="item.isDependency"
                  size="small"
                  color="blue"
                  variant="tonal"
                >
                  Dependency
                </v-chip>
                <span v-else class="text-medium-emphasis">Selected</span>
              </template>
              <template #[`item.status`]="{ item }">
                <v-tooltip
                  v-if="item.status === 'Error' && item.errorDetail"
                  location="top"
                  max-width="400"
                >
                  <template #activator="{ props }">
                    <v-chip
                      v-bind="props"
                      :color="changeStatusColour(item.status)"
                    >
                      {{ item.status }}
                      <v-icon end size="small">mdi-information-outline</v-icon>
                    </v-chip>
                  </template>
                  <span>{{ item.errorDetail }}</span>
                </v-tooltip>
                <v-chip v-else :color="changeStatusColour(item.status)">
                  {{ item.status }}
                </v-chip>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-icon
                  v-if="item.status === 'Pending'"
                  @click="deleteItem(item)"
                >
                  mdi-delete
                </v-icon>
              </template>
            </v-data-table>
            <v-alert
              v-if="
                selectedArray.some(
                  (item) => item.status === 'Error' && item.errorDetail
                )
              "
              type="error"
              variant="tonal"
              class="mt-4"
            >
              <div class="font-weight-bold mb-2">Error Details</div>
              <div
                v-for="item in selectedArray.filter(
                  (i) => i.status === 'Error' && i.errorDetail
                )"
                :key="item.name"
                class="text-body-2 mb-1"
              >
                <strong>{{ item.name }}</strong> ({{
                  objectTypeDisplayNames[item.type] || item.type
                }}): {{ item.errorDetail }}
              </div>
            </v-alert>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="currentStep > 1"
            variant="elevated"
            @click="prevStep"
            class="mr-auto"
            color="rgb(247, 247, 247)"
          >
            Back
          </v-btn>
          <v-btn
            v-if="currentStep > 1 && currentStep < 5"
            variant="elevated"
            @click="nextStep"
            color="primary"
          >
            Next
          </v-btn>
          <v-btn
            v-if="currentStep === 5"
            @click="showDeployDialog = true"
            variant="elevated"
            color="primary"
            :disabled="
              loadingDeploy || loadingResolutions || selectedArray.length === 0
            "
          >
            Deploy Configuration
          </v-btn>
        </v-card-actions>
      </v-sheet>

      <!-- Dialogs -->
      <v-dialog v-model="showFailedCredentialDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="warning"
                icon="mdi-alert-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">
                Credential Check Failed
              </div>
              <div>Verify the Jamf Pro URL and credentials are correct</div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="showFailedCredentialDialog = false"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog v-model="showWarningDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text
              ><h3>
                Please select at least one template before proceeding
              </h3></v-card-text
            >
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                class="flex-grow-1"
                variant="tonal"
                @click="showWarningDialog = false"
                >OK</v-btn
              >
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog v-model="showDeployDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text>
              <h3>
                You are about to deploy the selected templates into the targeted
                Jamf Pro instance. Please do not close or refresh this page
                until complete. Are you sure you want to proceed?
              </h3>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn @click="showDeployDialog = false">Cancel</v-btn>
              <v-btn
                @click="deploySelectedItems"
                class="flex-grow-1"
                variant="tonal"
              >
                Proceed
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog v-model="showCompletionDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="success"
                icon="mdi-check-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">Deployment Completed</div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                v-if="
                  hasDeployedPolicies &&
                  permissions.$state['Dashboard-PolicyUpdates'] &&
                  permissions.$state[
                    'Dashboard-PolicyUpdates'
                  ]?.actions?.includes('Create')
                "
                color="primary"
                min-width="200"
                variant="elevated"
                class="mb-2"
                @click="handleCheckSubscriptions"
              >
                Check Policy Subscriptions
              </v-btn>
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="showCompletionDialog = false"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog
        v-model="showCompletionWarningDialog"
        max-width="500"
        persistent
      >
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="warning"
                icon="mdi-alert-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">Completed with Errors</div>
              <div style="padding: 0 20px">
                Some items failed to deploy. Check the status and try again.
              </div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                v-if="
                  hasDeployedPolicies &&
                  permissions['Dashboard-PolicyUpdates'] &&
                  permissions['Dashboard-PolicyUpdates']?.actions?.includes(
                    'Create'
                  )
                "
                color="primary"
                min-width="200"
                variant="elevated"
                class="mb-2"
                @click="handleCheckSubscriptions"
              >
                Check Policy Subscriptions
              </v-btn>
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="showCompletionWarningDialog = false"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog v-model="showSubscriptionDialog" max-width="700" persistent>
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon color="info" class="mr-2">mdi-update</v-icon>
            Policy Update Subscriptions
          </v-card-title>
          <v-divider />
          <v-card-text>
            <!-- Loading state -->
            <div v-if="loadingSubscriptions" class="text-center py-8">
              <v-progress-circular indeterminate color="primary" size="48" />
              <div class="mt-4">Checking for eligible subscriptions...</div>
            </div>
            <!-- No eligible policies found -->
            <div
              v-else-if="eligibleSubscriptions.length === 0"
              class="text-center py-8"
            >
              <v-icon color="grey" size="48" class="mb-4"
                >mdi-information-outline</v-icon
              >
              <div>No eligible policy subscriptions found.</div>
            </div>
            <!-- Eligible policies found -->
            <template v-else>
              <p class="mb-4">
                The following deployed policies are eligible for automatic
                package update subscriptions. When subscribed, the policy's
                package will be automatically updated when a new version is
                available.
              </p>
              <v-table density="compact">
                <thead>
                  <tr>
                    <th>Policy Name</th>
                    <th>App</th>
                    <th>Stream</th>
                    <th>Subscribe</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="policy in eligibleSubscriptions" :key="policy.id">
                    <td>{{ policy.name }}</td>
                    <td>{{ policy.selectedMatch.name }}</td>
                    <td>
                      <v-select
                        v-if="policy.matches.length > 1"
                        v-model="policy.selectedMatch"
                        :items="policy.matches"
                        :item-title="(m) => m.stream"
                        return-object
                        density="compact"
                        hide-details
                        variant="outlined"
                        style="min-width: 150px"
                      />
                      <span v-else>{{ policy.selectedMatch.stream }}</span>
                    </td>
                    <td>
                      <v-checkbox
                        v-model="policy.selected"
                        density="compact"
                        hide-details
                      />
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </template>
          </v-card-text>
          <v-divider />
          <v-card-actions class="pa-4">
            <v-btn variant="outlined" @click="showSubscriptionDialog = false">
              Close
            </v-btn>
            <v-spacer />
            <v-btn
              v-if="eligibleSubscriptions.length > 0"
              variant="elevated"
              color="primary"
              :loading="loadingSubscriptions"
              :disabled="loadingSubscriptions"
              @click="addSelectedSubscriptions"
            >
              Add Subscriptions
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showErrorActionDialog" max-width="600" persistent>
        <v-card v-if="errorActionItem">
          <v-card-title class="d-flex align-center">
            <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
            Deployment Error
          </v-card-title>
          <v-divider />
          <v-card-text>
            <div class="mb-3">
              <strong>{{ errorActionItem.name }}</strong>
              ({{
                objectTypeDisplayNames[errorActionItem.type] ||
                errorActionItem.type
              }})
            </div>
            <div class="error-detail-box">
              {{ errorActionItem.errorDetail }}
            </div>
          </v-card-text>
          <v-divider />
          <v-card-actions class="pa-4">
            <v-btn
              variant="outlined"
              color="error"
              @click="handleErrorAction('stop')"
            >
              Stop
            </v-btn>
            <v-spacer />
            <v-btn variant="tonal" @click="handleErrorAction('continue')">
              Skip & Continue
            </v-btn>
            <v-btn
              variant="elevated"
              color="primary"
              @click="handleErrorAction('retry')"
            >
              Retry
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-stepper-content>
  </v-stepper>
</template>

<style scoped>
.input-field {
  width: 50%;
}
.error-detail-box {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 300px;
  overflow-y: auto;
}
:deep(.staged-row) {
  background-color: rgba(255, 152, 0, 0.08);
}
.expanded-row {
  width: 100%;
  border-collapse: collapse;
}
.expanded-row th {
  background-color: rgb(210, 210, 210);
  border: 1px solid #a4a4a4;
}
.expanded-row td {
  padding: 1px;
  border: 1px solid #a4a4a4;
}
.expanded-row td:nth-child(1) {
  width: 30%;
}
.expanded-row td:nth-child(2) {
  width: 50%;
}
.expanded-row td:nth-child(3) {
  width: 20%;
  text-align: center;
}
</style>
